import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { backendErrorMessage } from '@/shared/utils/apiError';

interface RejectSubmissionModalProps {
    isOpen: boolean;
    guideNumber: number;
    exerciseNumber: number;
    onConfirm: (rejectedReason: string, notifyUser: boolean) => Promise<void>;
    onClose: () => void;
}

export default function RejectSubmissionModal({
    isOpen,
    guideNumber,
    exerciseNumber,
    onConfirm,
    onClose,
}: RejectSubmissionModalProps) {
    const [rejectedReason, setRejectedReason] = useState('');
    const [notifyUser, setNotifyUser] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleClose = () => {
        if (loading) return;
        setRejectedReason('');
        setNotifyUser(true);
        setError(null);
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!rejectedReason.trim()) return;
        setLoading(true);
        setError(null);
        try {
            await onConfirm(rejectedReason.trim(), notifyUser);
            setRejectedReason('');
            setNotifyUser(true);
        } catch (err) {
            const msg = backendErrorMessage(err) ?? 'Error al rechazar. Intentá de nuevo.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black opacity-50" onClick={handleClose} />

            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Rechazar envío</h3>
                    <button onClick={handleClose} disabled={loading} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        Vas a rechazar el envío de{' '}
                        <strong className="text-gray-900">Guía {guideNumber}, Ejercicio {exerciseNumber}</strong>.
                        La submission pasará a estar marcada como fallida.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Motivo de rechazo <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={rejectedReason}
                            onChange={(e) => setRejectedReason(e.target.value)}
                            rows={3}
                            placeholder="Describí el motivo..."
                            disabled={loading}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 resize-none"
                        />
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={notifyUser}
                            onChange={(e) => setNotifyUser(e.target.checked)}
                            disabled={loading}
                            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Notificar al usuario por email</span>
                    </label>

                    {error && (
                        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            {error}
                        </p>
                    )}

                    <div className="flex gap-3 justify-end pt-2">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !rejectedReason.trim()}
                            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2"
                        >
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            Confirmar rechazo
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
